/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/react
 * @description Renders the Tailwind-based adapter status badge for the React
 * variant. The badge exposes the shared adapter status vocabulary in a compact,
 * themeable visual treatment.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "react";
import { cn } from "../lib/utils";

const statusBadgeBaseClassName =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-semibold uppercase transition-colors motion-reduce:transition-none";

const statusBadgeClassNames: Record<AdapterStatusType, string> = {
  connected: "border-primary/20 bg-primary/10 text-primary",
  disconnected: "border-border bg-muted text-muted-foreground",
  error: "border-destructive/20 bg-destructive/10 text-destructive",
  initializing: "border-accent/20 bg-accent/10 text-accent",
};

const statusDotClassNames: Record<AdapterStatusType, string> = {
  connected: "bg-primary",
  disconnected: "bg-muted-foreground",
  error: "bg-destructive",
  initializing: "bg-accent animate-pulse motion-reduce:animate-none",
};

const statusLabels: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

/**
 * Props for rendering the React adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant for the badge and status dot.
 * @property className Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Renders a compact React badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token, optional label visibility, and size variant.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge({
  status,
  showLabel = true,
  size = "sm",
  className,
}: AdapterStatusBadgeProps): JSX.Element {
  const badgeClassName = cn(
    statusBadgeBaseClassName,
    statusBadgeClassNames[status],
    size === "md" ? "text-xs" : "text-[0.625rem]",
    className,
  );
  const dot = (
    <span
      className={cn(
        "h-1.5 w-1.5 rounded-full",
        statusDotClassNames[status],
        size === "md" && "h-2 w-2",
      )}
    />
  );

  if (!showLabel) {
    return (
      <span
        aria-label={statusLabels[status]}
        className={badgeClassName}
        role="img"
      >
        {dot}
      </span>
    );
  }

  return (
    <span className={badgeClassName}>
      {dot}
      <span>{statusLabels[status]}</span>
    </span>
  );
}
